import {
  Mesh,
  DoubleSide,
  MeshBasicMaterial,
  TextureLoader,
  Vector3,
  PlaneGeometry,
  SphereGeometry,
  FrontSide,
  Sprite,
  SpriteMaterial,
  VideoTexture,
  LinearFilter,
  RGBFormat,
  RGBAFormat,
} from 'three';

import VideoJs from 'video.js';

import { VRObject3D } from './VRObject3D';
import { cornerHandleLocalFromVertex, vertexLocalFromCornerHandle, isIOS, isSafari } from './helpers/worldHelpers';
import { dragTexture, meshTextures, myLocationTexture, videoPlayer as videoPlayerIconTexture } from './helpers/texturesAndGeometries';
import { ICON } from "../Icons/Icons";
import { ComponentRegistry } from '../../constants/componentRegistry.js';
import { SCENE_INTERACTION } from '../../constants/scene';
import { SCENE_EVENTS } from '../MVC/Model';

/** Same base plane as VRVideoGallery — video metadata only adjusts corner positions. */
const SCENE_3D_PLANE_SIZE = 256;

const videoModalWrapper = (uuid) => `
<div class="modal video-player-modal video-player-webgl-modal" id="video-player-webgl-modal-${uuid}">
  <div class="modal-background"></div>
  <div class="modal-content video-player-modal__content">
    <div class="video-window">
      <div class="video-window__header">
        <div class="video-window__title"></div>
        <button class="video-window__close-btn" aria-label="Close">
          ${ICON.CLOSE}
        </button>
      </div>
      <div class="video-window__body">
        <div class="video-aspect-ratio-placeholder" id="video-placeholder-webgl-${uuid}"></div>
        <div class="video-loading-overlay" id="video-loading-webgl-${uuid}">
          <div class="video-loading-overlay__content">
            <div class="video-loading-overlay__spinner"></div>
            <div class="video-loading-overlay__text">Loading video...</div>
            <div class="video-loading-overlay__progress">
              <div class="video-loading-overlay__progress-bar" id="video-progress-bar-webgl-${uuid}"></div>
            </div>
            <div class="video-loading-overlay__percentage" id="video-progress-text-webgl-${uuid}">0%</div>
          </div>
        </div>
        <video id="video-player-webgl-${uuid}" class="video-js vjs-big-play-centered" controls preload="auto">
          <p class="vjs-no-js">
            To view this video please enable JavaScript, and consider upgrading to a web browser that supports HTML5 video
          </p>
        </video>
      </div>
    </div>
  </div>
</div>
`;

class VRVideoPlayerWebGL extends VRObject3D {

  constructor(containerName, camera, texture, x, y, z, model) {
    super(containerName);
    this.objConfig.dragOnlyWhenSelected = true;
    this.objConfig.isResizable = true;
    this.objConfig.isRotatable = false;
    this.objConfig.cubeSize = 20
    this.objConfig.offset = {
      topLeft: { x: 2, y: -3.5 },
      topRight: { x: -2, y: -3.5 },
      bottomRight: { x: -2, y: 3.5 },
      bottomLeft: { x: 2, y: 3.5 }
    }
    this.limits = {
      min: 200,   // New min value
      max: 900,   // New max value
      distance: 500 // New distance value
    };
    this.perspectivePosition = {
      "topLeft": new Vector3(-32, 32, 0),
      "topRight": new Vector3(32, 32, 0),
      "bottomRight": new Vector3(32, -32, 0),
      "bottomLeft": new Vector3(-32, -32, 0),
    };
    this.originalPerspectivePosition = {
      "topLeft": new Vector3(-32, 32, 0),
      "topRight": new Vector3(32, 32, 0),
      "bottomRight": new Vector3(32, -32, 0),
      "bottomLeft": new Vector3(-32, -32, 0),
    };

    this.dragCubeCorners = {};
    this.dragSprites = {};
    this.cornerPoints = {};
    this.centerHandle = null;
    this.centerHandleSprite = null;

    this.model = model;
    this.camera = camera;
    this.useIcon = false;
    this.placeAs3dElement = true;
    this.customIcon = true;
    this.videoPlayerImage = null; // Will store the uploaded image URL
    this.videoThumbnail = null; // Panel thumbnail / fallback before first frame loads
    this.placeholderImage = null; // Optional 3D mesh placeholder (overrides first frame)
    this._sceneVideoLoadedUrl = null;
    this._scenePlaybackActive = false;
    this._pendingPlaybackStart = false;

    const { video, videoTexture } = this.createEmptyVideoTexture();
    this.sceneVideo = video;
    this.sceneVideoTexture = videoTexture;
    this.material = new MeshBasicMaterial({ side: DoubleSide, transparent: true, map: texture || videoPlayerIconTexture });
    this.geometry = new PlaneGeometry(64, 64);
    this.mesh = new Mesh(this.geometry, this.material);
    this.mesh.type = 'videoPlayerWebGL';
    this.mesh.name = 'videoPlayerWebGL';
    this.name = 'videoPlayerWebGL';
    this.type = 'videoPlayerWebGL';
    // this.mesh.rotation.z = -Math.PI / 2; // Rotate -90 degrees around the Z-axis
    this.borderMesh = new Mesh(this.geometry, new MeshBasicMaterial({ side: FrontSide, transparent: true, color: "white", opacity: 0 }));
    this.borderMesh.name = "videoPlayerWebGLBorder"
    if (!this.editMode) {
      this.mesh.add(this.borderMesh);
    }

    this.loader = new TextureLoader();

    // INIT
    this.camera !== null ? this._setInitialPosRot() : this.setPosition(x, y, z);

    // Scale down the mesh to match imageGallery size
    this.mesh.scale.set(1, 1, 1);

    this.add(this.mesh);

    this.mesh.groupDetails = { title: $trans('info-element'), icon: 'info', editTitle: $trans('edit-video-background-text'), dataLang: 'edit-video-background-text' }

    this.editControls();

    this.updateDragBehavior(true);

    // Initialize video modal
    this.uuid = new aptis.UUIDGenerator().generate();
    this.videoUrl = null;
    this.videoTitle = null;
    this.videoPlayer = null;

    // Create modal wrapper
    this.modalWrapper = $(videoModalWrapper(this.uuid));
    $("body").append(this.modalWrapper);

    // Setup close button
    this.closeBtn = this.modalWrapper.find(".video-window__close-btn");
    this.closeBtn.on("click", () => this.closeModal());

    // Close on background click
    this.modalWrapper.find(".modal-background").on("click", () => this.closeModal());

    this.model.addEventListener(SCENE_INTERACTION.PLAY_ALL_VIDEOS_IN_VIDEO_GALLERY, () => {
      if (this.placeAs3dElement && this.shouldAutoplaySceneVideo()) {
        this.playOnClick();
      }
    });
    this.model.addEventListener(SCENE_EVENTS.START_SCENE_CHANGE, () => {
      this.pauseSceneVideo();
    });
    this.model.addEventListener(SCENE_EVENTS.TINY_WORLD_ANIMATION_ENDED, () => {
      this.restoreMeshVisibility();
    });
    this.model.addEventListener(SCENE_EVENTS.STOP_SCENE_CHANGE, this.restoreMeshVisibility);

    this._ensureSceneVideoLifecycleListeners();
  }

  _getVideoEventSourceId() {
    return this.dbConfig?.id ?? this.getId?.() ?? this.id ?? this.unique_id;
  }

  _fireVideoEvent(eventType) {
    if (!eventType || this.model?.editMode) return;
    const sourceId = this._getVideoEventSourceId();
    if (sourceId == null) return;
    this.model?.view?._executeEventActions?.(sourceId, eventType);
  }

  _ensureSceneVideoLifecycleListeners() {
    const video = this.sceneVideo;
    if (!video || video.dataset.videoLifecycleBound) return;
    video.dataset.videoLifecycleBound = '1';
    video.addEventListener('playing', () => {
      if (this._pendingPlaybackStart) {
        this._pendingPlaybackStart = false;
        this._onSceneVideoPlaybackStarted();
      }
    });
    video.addEventListener('ended', () => {
      if (!video.loop) {
        this._onSceneVideoPlaybackStopped();
      }
    });
  }

  _onSceneVideoPlaybackStarted() {
    if (this.model?.editMode) return;
    if (!this._scenePlaybackActive) {
      this._scenePlaybackActive = true;
      this._fireVideoEvent('videoStartedPlaying');
    }
  }

  _onSceneVideoPlaybackStopped() {
    if (!this._scenePlaybackActive) return;
    this._scenePlaybackActive = false;
    this._fireVideoEvent('stoppedPlaying');
  }

  _startSceneVideoPlayback({ loop = null } = {}) {
    if (!this.placeAs3dElement || !this.videoUrl || !this.sceneVideo) return;

    const url = this.videoUrl;
    this.material.map = this.sceneVideoTexture;
    this.material.needsUpdate = true;
    if (loop !== null) {
      this.sceneVideo.loop = !!loop;
    } else {
      this.applySceneVideoLoop();
    }

    const beginPlay = () => {
      this._pendingPlaybackStart = true;
      if (!this.sceneVideo.paused) {
        this.sceneVideo.muted = false;
        this.sceneVideo.volume = 1.0;
        this._pendingPlaybackStart = false;
        this._onSceneVideoPlaybackStarted();
        if (this.sceneVideoTexture) {
          this.sceneVideoTexture.needsUpdate = true;
        }
        return;
      }
      this.sceneVideo.play().then(() => {
        this.sceneVideo.muted = false;
        this.sceneVideo.volume = 1.0;
        if (this.sceneVideoTexture) {
          this.sceneVideoTexture.needsUpdate = true;
        }
      }).catch((error) => {
        this._pendingPlaybackStart = false;
        console.warn('Video play failed:', error);
      });
    };

    if (this._sceneVideoLoadedUrl !== url) {
      this._pendingPlaybackStart = true;
      this.setVideoSource(this.sceneVideo, url, (vw, vh) => {
        this._sceneVideoLoadedUrl = url;
        if (this.sceneVideoTexture) {
          this.sceneVideoTexture.needsUpdate = true;
        }
        const w = Number(vw);
        const h = Number(vh);
        if (Number.isFinite(w) && w > 0 && Number.isFinite(h) && h > 0) {
          this.applyScene3dVideoDimensions(w, h, false);
        }
      });
      return;
    }

    beginPlay();
  }

  playOnce() {
    if (!this.videoUrl) return;
    if (!this.placeAs3dElement) {
      this.openModal?.();
      if (this.videoPlayer) {
        this.videoPlayer.loop(false);
        this.videoPlayer.play?.();
      }
      return;
    }
    this._startSceneVideoPlayback({ loop: false });
  }

  playLoop() {
    if (!this.videoUrl) return;
    if (!this.placeAs3dElement) {
      this.openModal?.();
      if (this.videoPlayer) {
        this.videoPlayer.loop(true);
        this.videoPlayer.play?.();
      }
      return;
    }
    this._startSceneVideoPlayback({ loop: true });
  }

  stopPlaying() {
    if (!this.placeAs3dElement) {
      this.videoPlayer?.pause?.();
      this.closeModal?.();
      return;
    }
    const wasActive = this._scenePlaybackActive;
    this.pauseSceneVideoPlayback();
    if (wasActive) {
      this._onSceneVideoPlaybackStopped();
    }
  }

  hasTransparentBackground(conf = this.dbConfig) {
    const value = conf?.transparentBackground ?? this.dbConfig?.transparentBackground;
    return value === true || value === 'true' || value === 1 || value === '1';
  }

  applyVideoTransparency(conf = this.dbConfig) {
    const useAlpha = this.hasTransparentBackground(conf);
    if (this.sceneVideoTexture) {
      this.sceneVideoTexture.format = useAlpha ? RGBAFormat : RGBFormat;
      this.sceneVideoTexture.premultiplyAlpha = false;
      this.sceneVideoTexture.needsUpdate = true;
    }
    if (this.material) {
      this.material.transparent = true;
      this.material.depthWrite = !useAlpha;
      this.material.needsUpdate = true;
    }
  }

  createEmptyVideoTexture() {
    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.playsInline = true;
    video.loop = true;
    video.muted = true;
    video.volume = 1.0;
    video.setAttribute('playsinline', 'true');
    video.setAttribute('webkit-playsinline', 'true');
    video.setAttribute('x5-playsinline', 'true');
    video.disablePictureInPicture = true;
    video.controls = false;

    const videoTexture = new VideoTexture(video);
    videoTexture.minFilter = LinearFilter;
    videoTexture.magFilter = LinearFilter;
    videoTexture.format = RGBFormat;
    videoTexture.premultiplyAlpha = false;

    return { video, videoTexture };
  }

  restoreMeshVisibility = () => {
    if (!this.placeAs3dElement || !this.mesh) return;
    if (!this.model.editMode) {
      const hiddenByConfig = this.dbConfig?.hideOnStart === true;
      const hiddenByGlobalToggle = this.model.showElements === false;
      if (hiddenByConfig || hiddenByGlobalToggle) return;
    }
    this.mesh.visible = true;
    if (this.mesh.material && !Array.isArray(this.mesh.material)) {
      this.mesh.material.opacity = 1;
      this.mesh.material.needsUpdate = true;
    }
  }

  pauseSceneVideo() {
    if (this.sceneVideo) {
      this.sceneVideo.pause();
      this.sceneVideo.src = '';
    }
  }

  updateCustomIconState() {
    this.customIcon = true;
  }

  getDefaultScene3dPerspectivePosition() {
    const half = SCENE_3D_PLANE_SIZE / 2;
    return {
      topLeft: new Vector3(-half, half, 0),
      topRight: new Vector3(half, half, 0),
      bottomRight: new Vector3(half, -half, 0),
      bottomLeft: new Vector3(-half, -half, 0),
    };
  }

  _hasDegeneratePerspectiveCorners() {
    const { topLeft, topRight, bottomRight, bottomLeft } = this.perspectivePosition;
    if (!topLeft || !topRight || !bottomRight || !bottomLeft) return true;
    const samePoint = (a, b) => a.x === b.x && a.y === b.y;
    return samePoint(topLeft, bottomRight) || samePoint(topRight, bottomLeft);
  }

  ensureScene3dMeshGeometry() {
    const { width, height } = this.mesh.geometry?.parameters || {};
    if (width === SCENE_3D_PLANE_SIZE && height === SCENE_3D_PLANE_SIZE) {
      return;
    }

    if (this.mesh.geometry) {
      this.mesh.geometry.dispose();
    }
    this.mesh.geometry = new PlaneGeometry(SCENE_3D_PLANE_SIZE, SCENE_3D_PLANE_SIZE);
    this.mesh.geometry.computeBoundingBox();
    this.mesh.geometry.computeBoundingSphere();

    if (this.borderMesh) {
      if (this.borderMesh.geometry) {
        this.borderMesh.geometry.dispose();
      }
      this.borderMesh.geometry = new PlaneGeometry(SCENE_3D_PLANE_SIZE, SCENE_3D_PLANE_SIZE);
    }

    this.mesh.updateMatrixWorld(true);
  }

  resetScene3dPerspectivePositions() {
    const corners = this.getDefaultScene3dPerspectivePosition();
    if (!this.dbConfig.original) this.dbConfig.original = {};
    Object.entries(corners).forEach(([name, vector]) => {
      this.perspectivePosition[name] = vector.clone();
      this.originalPerspectivePosition[name] = vector.clone();
      this.dbConfig[name] = vector.clone();
      this.dbConfig.original[name] = vector.clone();
    });
  }

  hasScene3dPerspectiveCorners(conf = this.dbConfig) {
    if (!conf?.scene3dPerspectiveApplied) return false;
    return ['topLeft', 'topRight', 'bottomRight', 'bottomLeft'].every((name) => {
      const p = conf?.[name];
      return p && Number.isFinite(p.x) && Number.isFinite(p.y);
    });
  }

  hasPerspectiveCorners(conf = this.dbConfig) {
    return ['topLeft', 'topRight', 'bottomRight', 'bottomLeft'].every((name) => {
      const p = conf?.[name];
      return p && Number.isFinite(p.x) && Number.isFinite(p.y);
    });
  }

  syncPerspectiveToDbConfig() {
    if (!this.dbConfig.original) this.dbConfig.original = {};
    ['topLeft', 'topRight', 'bottomRight', 'bottomLeft'].forEach((name) => {
      if (this.perspectivePosition?.[name]) {
        this.dbConfig[name] = this.perspectivePosition[name].clone();
      }
      if (this.originalPerspectivePosition?.[name]) {
        this.dbConfig.original[name] = this.originalPerspectivePosition[name].clone();
      }
    });
    if (this.placeAs3dElement && this.hasPerspectiveCorners()) {
      this.dbConfig.scene3dPerspectiveApplied = true;
    }
  }

  getPerspectiveSavePayload() {
    this.syncPerspectiveToDbConfig();
    const payload = {
      placeAs3dElement: this.placeAs3dElement,
      scene3dPerspectiveApplied: this.dbConfig.scene3dPerspectiveApplied === true,
      autoplay: this.dbConfig.autoplay === true || this.dbConfig.autoplay === '2',
      playLoop: this.dbConfig.playLoop !== false && this.dbConfig.playLoop !== 'false' && this.dbConfig.playLoop !== '0',
      transparentBackground: this.hasTransparentBackground(this.dbConfig),
      original: {},
    };
    ['topLeft', 'topRight', 'bottomRight', 'bottomLeft'].forEach((corner) => {
      if (this.dbConfig[corner]) {
        payload[corner] = this.dbConfig[corner].clone?.() ?? { ...this.dbConfig[corner] };
      }
      if (this.dbConfig.original?.[corner]) {
        payload.original[corner] = this.dbConfig.original[corner].clone?.()
          ?? { ...this.dbConfig.original[corner] };
      }
    });
    return payload;
  }

  applyScene3dVideoDimensions(w, h, resetCorners = true) {
    const width = Number(w);
    const height = Number(h);
    if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
      return;
    }

    this.ensureScene3dMeshGeometry();
    this.updateGeometryWithVideoDimensions(width, height, resetCorners);
    this.applyPerspectiveDragAndDrop();
    this.ensurePerspectiveDragPoints();
  }

  ensurePerspectiveDragPoints() {
    if (!this.model?.editMode) return;

    if (!this.dragCubeCorners?.topLeft) {
      this.addDragPoints();
    } else {
      this.applyPerspectiveDragAndDrop();
    }

    if (this.isSelected) {
      this.showDragHandles();
    } else {
      this.hideDragHandles();
    }
  }

  getLimits() {
    const min = Number(this.limits?.min) || 200;
    const max = Number(this.limits?.max) || 900;
    const configuredDistance = Number(this.dbConfig?.distance);
    const runtimeDistance = Number(this.limits?.distance);
    const distance = Number.isFinite(configuredDistance) && configuredDistance > 0
      ? configuredDistance
      : (Number.isFinite(runtimeDistance) && runtimeDistance > 0 ? runtimeDistance : 500);

    return { min, max, distance };
  }

  setDistance(val) {
    const n = Number(val);
    if (!Number.isFinite(n) || n <= 0) return;
    const { min, max } = this.getLimits();
    const clamped = Math.min(max, Math.max(min, n));
    this.limits.distance = clamped;
    this.dbConfig = { ...this.dbConfig, distance: clamped };
  }

  /**
   * Calculate dimensions based on aspect ratio
   * Uses 20% of screen size for the longer dimension
   * @param {number} aspectRatio - Image aspect ratio
   * @returns {{w: number, h: number}} Width and height
   */
  calculateDimensionsFromAspectRatio(aspectRatio) {
    // Get screen dimensions (20% of screen size)
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;
    const targetSize = Math.min(screenWidth, screenHeight) * 0.2;

    let w = 1, h = 1;
    if (aspectRatio < 1) { // Portrait (height > width)
      h = targetSize;
      w = targetSize * aspectRatio;
    } else { // Landscape or square (width >= height)
      w = targetSize;
      h = targetSize / aspectRatio;
    }
    return { w, h };
  }

  /**
   * Update mesh geometry with new dimensions
   * @param {number} width - New width
   * @param {number} height - New height
   */
  updateMeshGeometry(width, height) {
    // Dispose old geometry
    if (this.mesh.geometry) {
      this.mesh.geometry.dispose();
    }

    // Create new geometry
    this.mesh.geometry = new PlaneGeometry(width, height);
    this.mesh.geometry.computeBoundingBox();
    this.mesh.geometry.computeBoundingSphere();
    this.mesh.updateMatrixWorld(true);
  }

  /**
   * Reset perspective corner positions based on dimensions
   * @param {number} width - Mesh width
   * @param {number} height - Mesh height
   */
  resetPerspectivePositions(width, height) {
    const halfW = width / 2;
    const halfH = height / 2;
    const corners = {
      "topLeft": new Vector3(-halfW, halfH, 0),
      "topRight": new Vector3(halfW, halfH, 0),
      "bottomRight": new Vector3(halfW, -halfH, 0),
      "bottomLeft": new Vector3(-halfW, -halfH, 0)
    };

    Object.entries(corners).forEach(([name, vector]) => {
      this.perspectivePosition[name] = vector;
      this.dbConfig[name] = vector.clone();
      this.dbConfig.original[name] = vector.clone();
      this.originalPerspectivePosition[name] = vector.clone();
    });
  }

  resetPerspective = () => {
    this.removeDragPoints();

    if (this.placeAs3dElement && this.videoUrl) {
      const w = Number(this.videoWidth);
      const h = Number(this.videoHeight);
      if (Number.isFinite(w) && w > 0 && Number.isFinite(h) && h > 0) {
        this.applyScene3dVideoDimensions(w, h, true);
      }
      const afterLoad = (vw, vh) => {
        this.applyScene3dVideoDimensions(vw, vh, true);
      };
      this.loadFirstFrame(this.sceneVideo, this.videoUrl, afterLoad);
      return;
    }

    if (this.placeAs3dElement) {
      this.ensureScene3dMeshGeometry();
      this.resetScene3dPerspectivePositions();
      this.applyPerspectiveDragAndDrop();
      this.ensurePerspectiveDragPoints();
      return;
    }

    // Check if there's a valid texture with image
    const texture = this.mesh.material.map;
    if (this.dbConfig?.images?.length) {
      if (texture && texture.image && texture.image.width && texture.image.height) {
        // Has image - reset to image's original dimensions
        let aspectRatio = texture.image.width / texture.image.height;
        if (aspectRatio === undefined || aspectRatio === 0) {
          aspectRatio = 16 / 9;
        }

        const { w, h } = this.calculateDimensionsFromAspectRatio(aspectRatio);
        this.updateMeshGeometry(w, h);
        this.resetValuesWithNewTexture(texture);
      }
    } else {
      // No image - reset to default size (64x64)
      this.updateMeshGeometry(64, 64);
      this.resetPerspectivePositions(64, 64);
    }

    this.applyPerspectiveDragAndDrop();
    this.addDragPoints();
  }

  _shouldReapplySceneVideo(conf) {
    if (!conf || typeof conf !== 'object') return false;
    return ['placeAs3dElement', 'autoplay', 'playLoop', 'transparentBackground', 'videoUrl', 'videoWidth', 'videoHeight', 'videoRatio', 'videoThumbnail', 'placeholderImage', 'customImage']
      .some((key) => Object.prototype.hasOwnProperty.call(conf, key));
  }

  setDbConfig = (conf) => {
    const normalizedConf = { ...conf, placeAs3dElement: true, useIcon: false };
    super.setDbConfig(normalizedConf);
    const reapplySceneVideo = this._shouldReapplySceneVideo(normalizedConf);
    const savedSize = Number(this.dbConfig?.size);
    if (Number.isFinite(savedSize) && savedSize > 0) {
      this.mesh.scale.set(savedSize, savedSize, savedSize);
    }

    this.placeAs3dElement = true;
    this.useIcon = false;
    if (normalizedConf.customImage !== undefined) {
      this.videoPlayerImage = normalizedConf.customImage || null;
    }

    this.updateCustomIconState();

    // Restore video URL, title, and dimensions
    if (normalizedConf.videoUrl) {
      this.videoUrl = normalizedConf.videoUrl;
      this.videoTitle = normalizedConf.videoTitle || null;
      this.videoWidth = normalizedConf.videoWidth || null;
      this.videoHeight = normalizedConf.videoHeight || null;
      this.videoRatio = normalizedConf.videoRatio || null;
    }
    if (normalizedConf.videoThumbnail !== undefined) {
      this.videoThumbnail = normalizedConf.videoThumbnail || null;
    }
    if (normalizedConf.placeholderImage !== undefined) {
      this.placeholderImage = normalizedConf.placeholderImage || null;
    }

    if (normalizedConf.transparentBackground !== undefined) {
      this.dbConfig.transparentBackground = !!normalizedConf.transparentBackground;
    }
    this.applyVideoTransparency(this.dbConfig);

    if (normalizedConf.scene3dPerspectiveApplied !== undefined) {
      this.dbConfig.scene3dPerspectiveApplied = !!normalizedConf.scene3dPerspectiveApplied;
    }

    // Only restore saved corners for 3D mode when they were saved from a prior 3D video apply.
    const restoreCorners = this.hasScene3dPerspectiveCorners(this.dbConfig);
    if (restoreCorners) {
      Object.entries(this.perspectivePosition).forEach(([name]) => {
        const prop = this.dbConfig[name];
        if (prop) {
          this.perspectivePosition[name] = new Vector3(prop.x, prop.y, prop.z || 0);
        }
      });
    }

    if (this._hasDegeneratePerspectiveCorners() || !this.hasScene3dPerspectiveCorners(this.dbConfig)) {
      this.resetScene3dPerspectivePositions();
    }

    this.ensureScene3dMeshGeometry();
    this.objConfig.dragOnlyWhenSelected = true;
    if (reapplySceneVideo) {
      this.applySceneVideoFromConfig(this.dbConfig);
    } else {
      this.applyPerspectiveDragAndDrop();
    }
    if (this.model.editMode) {
      this.ensurePerspectiveDragPoints();
    }

    this.dbConfig.placeAs3dElement = true;
    this.dbConfig.useIcon = false;
  }

  shouldAutoplaySceneVideo(conf = this.dbConfig) {
    const value = conf?.autoplay ?? this.dbConfig?.autoplay;
    return value === true || value === 'true' || value === '2' || value === 2;
  }

  shouldPlayLoop(conf = this.dbConfig) {
    const value = conf?.playLoop ?? this.dbConfig?.playLoop;
    if (value === undefined || value === null) return true;
    if (value === false || value === 'false' || value === '0' || value === 0) return false;
    return true;
  }

  applySceneVideoLoop(conf = this.dbConfig) {
    if (this.sceneVideo) {
      this.sceneVideo.loop = this.shouldPlayLoop(conf);
    }
  }

  isDefaultPlaceholderImage(url) {
    return !url || String(url).includes('edit_play_video.png');
  }

  getScenePlaceholderImageUrl(conf = this.dbConfig) {
    return conf?.placeholderImage || this.placeholderImage || this.dbConfig?.placeholderImage || null;
  }

  applySceneVideoFromConfig(conf = this.dbConfig, firstTime = null) {
    if (!this.placeAs3dElement) {
      return;
    }

    this.ensureScene3dMeshGeometry();
    this.applyVideoTransparency(conf);
    this.applySceneVideoLoop(conf);

    const placeholderUrl = this.getScenePlaceholderImageUrl(conf);
    const url = conf?.videoUrl || this.videoUrl;
    const w = Number(conf?.videoWidth ?? this.videoWidth);
    const h = Number(conf?.videoHeight ?? this.videoHeight);
    const hasScene3dCorners = this.hasScene3dPerspectiveCorners(this.dbConfig);
    const resetCorners = firstTime ?? !hasScene3dCorners;

    if (Number.isFinite(w) && w > 0 && Number.isFinite(h) && h > 0) {
      this.applyScene3dVideoDimensions(w, h, resetCorners);
    } else if (!url) {
      if (!this.hasScene3dPerspectiveCorners(this.dbConfig)) {
        this.resetScene3dPerspectivePositions();
      }
      this.applyPerspectiveDragAndDrop();
      this.ensurePerspectiveDragPoints();
    } else {
      this.applyPerspectiveDragAndDrop();
      this.ensurePerspectiveDragPoints();
    }

    if (this.model.editMode) {
      this.mesh.visible = true;
    }

    if (!this.model.editMode) {
      this.mesh.remove(this.borderMesh);
    }

    // Uploaded placeholder image takes priority over the first video frame.
    if (!this.isDefaultPlaceholderImage(placeholderUrl)) {
      this.loadScenePlaceholderImage(placeholderUrl);
      return;
    }

    if (!url) {
      const thumb = conf?.videoThumbnail || this.videoThumbnail || this.dbConfig?.videoThumbnail;
      if (thumb) {
        this.loadScenePlaceholderImage(thumb);
      } else {
        this.material.map = videoPlayerIconTexture;
        this.material.needsUpdate = true;
      }
      return;
    }

    this.material.map = this.sceneVideoTexture;
    this.material.needsUpdate = true;

    const afterLoad = (vw, vh) => {
      const metaW = Number(vw);
      const metaH = Number(vh);
      const shouldReset = resetCorners
        && !this.hasScene3dPerspectiveCorners(this.dbConfig)
        && (Number.isFinite(w) && w > 0 && (metaW !== w || metaH !== h));
      this.applyScene3dVideoDimensions(metaW, metaH, shouldReset);
    };

    const onFrameReady = () => {
      this._sceneVideoLoadedUrl = url;
      if (this.sceneVideoTexture) {
        this.sceneVideoTexture.needsUpdate = true;
      }
      this.material.needsUpdate = true;
    };

    const autoplay = this.shouldAutoplaySceneVideo(conf);
    if (!this.model.editMode && autoplay) {
      this.setVideoSource(this.sceneVideo, url, (vw, vh) => {
        afterLoad(vw, vh);
        onFrameReady();
      });
    } else {
      this.loadFirstFrame(this.sceneVideo, url, afterLoad, onFrameReady);
    }
  }

  loadScenePlaceholderImage(url, callback = null) {
    if (!url) return;

    const applyTexture = (texture) => {
      this.material.map = texture;
      this.material.needsUpdate = true;
      if (this.model.editMode) {
        this.mesh.visible = true;
      }
      callback && callback();
    };

    if (meshTextures[url]) {
      applyTexture(meshTextures[url]);
      return;
    }

    this.loader.load(url, (texture) => {
      meshTextures[url] = texture;
      applyTexture(texture);
    });
  }

  clearPlaceholderImage(callback = null) {
    if (this.placeAs3dElement) {
      this.placeholderImage = null;
      this.dbConfig.placeholderImage = null;
      this._sceneVideoLoadedUrl = null;
    } else {
      this.videoPlayerImage = null;
      this.dbConfig.customImage = null;
    }
    this.updateMeshTexture(callback);
  }

  setPlaceAs3dElement = () => {
    this.useIcon = false;
    this.placeAs3dElement = true;
    this.dbConfig.useIcon = false;
    this.dbConfig.placeAs3dElement = true;
    this.updateCustomIconState();

    this.ensureScene3dMeshGeometry();
    const hasSaved = this.hasScene3dPerspectiveCorners();
    if (!hasSaved) {
      this.dbConfig.scene3dPerspectiveApplied = false;
      if (this.videoWidth && this.videoHeight) {
        this.applyScene3dVideoDimensions(this.videoWidth, this.videoHeight, true);
      } else {
        this.resetScene3dPerspectivePositions();
        this.applyPerspectiveDragAndDrop();
        this.ensurePerspectiveDragPoints();
      }
    } else {
      this.applyPerspectiveDragAndDrop();
      this.ensurePerspectiveDragPoints();
    }

    if (!this.videoUrl) {
      this.applySceneVideoFromConfig(this.dbConfig);
    }

    this.updateDragBehavior();
  }

  setUseIcon = () => {}

  updateDragBehavior = (isInitializing = false) => {
    this.ensureScene3dMeshGeometry();
    this.objConfig.dragOnlyWhenSelected = true;
    if (this.model.editMode && !isInitializing) {
      this.ensurePerspectiveDragPoints();
    }
    this.applySceneVideoFromConfig({
      ...this.dbConfig,
      videoUrl: this.videoUrl,
      videoWidth: this.videoWidth,
      videoHeight: this.videoHeight,
      videoThumbnail: this.videoThumbnail,
      placeholderImage: this.placeholderImage,
    });
  }

  updateMeshTexture = (imageLoadCallback = null) => {
    this.applySceneVideoFromConfig({
      ...this.dbConfig,
      videoUrl: this.videoUrl,
      videoWidth: this.videoWidth,
      videoHeight: this.videoHeight,
      videoThumbnail: this.videoThumbnail,
      placeholderImage: this.placeholderImage,
    });
    imageLoadCallback && imageLoadCallback();
  }

  /**
   * Load custom image for video player
   * Used when uploading new image or reloading page with saved custom image
   */
  loadCustomImage(url, callback = null, resetPerspective = false) {
    // Check if texture is already cached
    if (meshTextures[url]) {
      this.material.map = meshTextures[url];
      this.material.needsUpdate = true;
      this.applyPerspectiveDragAndDrop();
      callback && callback();
      return;
    }

    // Load new texture
    this.loader.load(url, (texture) => {
      // Calculate aspect ratio and dimensions
      let aspectRatio = texture.image.width / texture.image.height;
      if (aspectRatio === undefined || aspectRatio === 0) {
        aspectRatio = 16 / 9; // Default aspect ratio
      }

      const { w, h } = this.calculateDimensionsFromAspectRatio(aspectRatio);

      // Cache texture
      meshTextures[url] = texture;

      // Update material
      this.material.map = texture;
      this.material.needsUpdate = true;

      // Update geometry to match image dimensions
      this.updateMeshGeometry(w, h);

      // Only reset perspective positions if NOT loading from saved config
      // When loading from config, we want to preserve the saved corner positions
      if (resetPerspective) {
        // Reset perspective positions based on new dimensions (new upload)
        this.resetPerspectivePositions(w, h);
      } else {
        // Loading from config - just update originalPerspectivePosition for reference
        const halfW = w / 2, halfH = h / 2;
        const corners = {
          "topLeft": new Vector3(-halfW, halfH, 0),
          "topRight": new Vector3(halfW, halfH, 0),
          "bottomRight": new Vector3(halfW, -halfH, 0),
          "bottomLeft": new Vector3(-halfW, -halfH, 0)
        };
        Object.entries(corners).forEach(([name, vector]) => {
          this.originalPerspectivePosition[name] = vector.clone();
        });
      }

      if (callback) { callback(); }

      this.removeDragPoints();
      this.applyPerspectiveDragAndDrop();
      this.model.editMode && this.addDragPoints();
    });
  }


  resetValuesWithNewTexture(texture) {
    let aspectRatio = texture.image.width / texture.image.height;
    if (aspectRatio === undefined || aspectRatio === 0) {
      aspectRatio = 16 / 9;
    }

    const { w, h } = this.calculateDimensionsFromAspectRatio(aspectRatio);
    this.resetPerspectivePositions(w, h);
  }

  applyPerspectiveDragAndDrop() {
    const points = this.perspectivePosition;
    const positionAttribute = this.mesh.geometry.attributes.position;
    const uvAttribute = this.mesh.geometry.attributes.uv;

    // Mapping the corners to the indices based on their order in the mesh's vertices
    const cornerMappings = {
      topLeft: 0,
      topRight: 1,
      bottomRight: 3,
      bottomLeft: 2
    };

    // Loop through each corner and update its position
    Object.entries(cornerMappings).forEach(([corner, index]) => {
      if (points[corner]) {
        positionAttribute.setXY(index, points[corner].x, points[corner].y);

        if (this.dragCubeCorners && this.dragCubeCorners[corner]) {
          const handle = cornerHandleLocalFromVertex(points[corner], this.objConfig, corner);
          this.dragCubeCorners[corner].position.set(handle.x, handle.y, handle.z);
        }
      }
    });

    // Update UVs to standard mapping
    uvAttribute.setXY(cornerMappings['topLeft'], 0, 1); // UV for topLeft
    uvAttribute.setXY(cornerMappings['topRight'], 1, 1); // UV for topRight
    uvAttribute.setXY(cornerMappings['bottomRight'], 1, 0); // UV for bottomRight
    uvAttribute.setXY(cornerMappings['bottomLeft'], 0, 0); // UV for bottomLeft

    positionAttribute.needsUpdate = true;
    uvAttribute.needsUpdate = true; // Notify Three.js that the UVs have changed

    // Update bounding box after deforming geometry
    this.mesh.geometry.computeBoundingBox();
    this.mesh.geometry.computeBoundingSphere();

    this.applyPerspectiveDragAndDropBorder()
    this.syncHandleVisuals();
    this.syncPerspectiveToDbConfig();
  }
  applyPerspectiveDragAndDropBorder() {
    const points = this.perspectivePosition;
    const positionAttribute = this.borderMesh.geometry.attributes.position;
    const uvAttribute = this.borderMesh.geometry.attributes.uv;

    // Mapping the corners to the indices based on their order in the mesh's vertices
    const cornerMappings = {
      topLeft: 0,
      topRight: 1,
      bottomRight: 3,
      bottomLeft: 2
    };

    // Loop through each corner and update its position
    Object.entries(cornerMappings).forEach(([corner, index]) => {
      if (points[corner]) {
        positionAttribute.setXY(index, points[corner].x, points[corner].y);
      }
    });

    // Update UVs to standard mapping
    uvAttribute.setXY(cornerMappings['topLeft'], 0, 1); // UV for topLeft
    uvAttribute.setXY(cornerMappings['topRight'], 1, 1); // UV for topRight
    uvAttribute.setXY(cornerMappings['bottomRight'], 1, 0); // UV for bottomRight
    uvAttribute.setXY(cornerMappings['bottomLeft'], 0, 0); // UV for bottomLeft

    positionAttribute.needsUpdate = true;
    uvAttribute.needsUpdate = true; // Notify Three.js that the UVs have changed

  }

  updateMeshCorner(cornerName, newPosition) {
    const positionAttribute = this.mesh.geometry.attributes.position;
    const cornerMappings = {
      topLeft: 0,
      topRight: 1,
      bottomRight: 3,
      bottomLeft: 2
    };

    const vertex = vertexLocalFromCornerHandle(newPosition, this.objConfig, cornerName);
    const vertexPosition = new Vector3(vertex.x, vertex.y, vertex.z);

    this.perspectivePosition[cornerName] = vertexPosition;
    this.dbConfig[cornerName] = vertexPosition.clone();
    if (this.placeAs3dElement) {
      this.dbConfig.scene3dPerspectiveApplied = true;
    }

    const vertexIndex = cornerMappings[cornerName];
    if (vertexIndex !== undefined) {
      positionAttribute.setXYZ(vertexIndex, vertexPosition.x, vertexPosition.y, vertexPosition.z);
      positionAttribute.needsUpdate = true;
      this.mesh.geometry.computeBoundingBox();
      this.mesh.geometry.computeBoundingSphere();
    }

    if (this.dragCubeCorners?.[cornerName]) {
      const handle = cornerHandleLocalFromVertex(vertexPosition, this.objConfig, cornerName);
      this.dragCubeCorners[cornerName].position.set(handle.x, handle.y, handle.z);
    }
    this.syncPerspectiveToDbConfig();
  }

  updateLivePosition(name, position) {
    this.dbConfig[name] = this.perspectivePosition[name].clone();
    this.perspectivePosition[name].copy(position);
    this.applyPerspectiveDragAndDrop();
  }

  onSelect() {
    if (!this.dragCubeCorners?.topLeft) {
      this.addDragPoints();
    }
    // Show drag handles when object is selected
    this.showDragHandles();
    // Hide border in edit mode
    if (this.model.editMode && this.borderMesh) {
      this.borderMesh.visible = false;
    }
    super.onSelect();
  }

  onDeselect() {
    this.syncPerspectiveToDbConfig();
    // Hide drag handles when object is deselected
    this.hideDragHandles();

    // Cleanup: Move settings back to modal when deselected
    const settings = $("#create-videoPlayer-settings");
    if (settings.closest(".object-settings-content").length) {
      $(".video-player-modal .modal-data").append(settings);
    }

    super.onDeselect();
  }

  showDragHandles() {
    Object.values(this.dragSprites).forEach(sprite => {
      if (sprite) sprite.visible = true;
    });
    Object.values(this.dragCubeCorners).forEach(cube => {
      if (cube) cube.visible = true;
    });
    if (this.centerHandle) this.centerHandle.visible = true;
    if (this.centerHandleSprite) this.centerHandleSprite.visible = true;
  }

  hideDragHandles() {
    Object.values(this.dragSprites).forEach(sprite => {
      if (sprite) sprite.visible = false;
    });
    Object.values(this.dragCubeCorners).forEach(cube => {
      if (cube) cube.visible = false;
    });
    if (this.centerHandle) this.centerHandle.visible = false;
    if (this.centerHandleSprite) this.centerHandleSprite.visible = false;
  }

  syncHandleVisuals = () => {
    const sx = this.mesh?.scale?.x || 1;
    const sy = this.mesh?.scale?.y || 1;
    const sz = this.mesh?.scale?.z || 1;
    const invScale = new Vector3(
      sx !== 0 ? 1 / sx : 1,
      sy !== 0 ? 1 / sy : 1,
      sz !== 0 ? 1 / sz : 1
    );

    Object.values(this.dragCubeCorners).forEach((cube) => {
      if (!cube) return;
      cube.scale.copy(invScale);
      cube.renderOrder = 10000;
    });

    if (this.centerHandle) {
      this.centerHandle.scale.copy(invScale);
      this.centerHandle.renderOrder = 10000;
    }
  }

  addDragPoints() {
    if (this.dragCubeCorners["topLeft"]) {
      return;
    }

    const useLinkStyle = true;
    const sphereRadius = this.objConfig.cubeSize / 2;
    const hitRadius = sphereRadius * (this.objConfig.cornerHitAreaScale || 1);
    const cubeGeometry = new SphereGeometry(hitRadius, 8, 8);
    const cubeMaterial = new MeshBasicMaterial({
      color: 0x6B9D11,
      opacity: 0,
      transparent: true,
      depthWrite: false,
      depthTest: false,
      renderOrder: 999,
    });

    // Check if the mesh's geometry's positions are accessible
    if (!this.mesh.geometry.attributes.position) {
      console.error("Mesh geometry does not have position attributes.");
      return;
    }

    const positionAttribute = this.mesh.geometry.attributes.position;
    if (positionAttribute.count < 4) {
      console.error("Mesh does not have enough vertices to define four corners.");
      return;
    }

    // Define corner positions by the first four vertices in the geometry
    // This assumes the vertices are laid out as:
    // 0 - topLeft, 1 - topRight, 3 - bottomRight, 2 - bottomLeft (depending on the draw order)
    const cornerNames = ['topLeft', 'topRight', 'bottomRight', 'bottomLeft'];
    const cornerIndices = [0, 1, 3, 2];  // Adjust these indices according to your specific geometry

    this.dragCubeCorners = {};  // Store references to the corner cubes

    cornerIndices.forEach((index, i) => {
      const offset = this.objConfig.offset[cornerNames[i]]

      // Access vertex positions
      const x = positionAttribute.getX(index) + (offset?.x || 0);
      const y = positionAttribute.getY(index) + (offset?.y || 0);
      const z = positionAttribute.getZ(index);

      // Create cube and position it at the vertex
      const cube = new Mesh(cubeGeometry, cubeMaterial);

      cube.name = cornerNames[i];  // Name the cube based on its corner
      const handleZ = useLinkStyle ? z + 1 : z;
      cube.position.set(x, y + this.objConfig.cubeSize / 2, handleZ);
      if (useLinkStyle) {
        cube.renderOrder = 10000;
      }

      // Create spherical background sprite for better raycasting from all angles
      const spriteRadius = this.objConfig.cubeSize / 2 * 1.1;
      const spriteGeometry = new SphereGeometry(spriteRadius, 16, 16);
      const spriteMaterial = new MeshBasicMaterial({
        color: 0x6B9D11, // Green color
        transparent: true,
        opacity: 0.5,
        depthTest: false,
        ...(useLinkStyle ? { depthWrite: false } : {}),
      });

      const sprite = new Mesh(spriteGeometry, spriteMaterial);
      sprite.name = `sprite_${cornerNames[i]}`;
      sprite.position.set(0, -this.objConfig.cubeSize / 2, 0);
      sprite.raycast = () => { };
      if (useLinkStyle) {
        sprite.renderOrder = 10001;
      }

      // Create icon sprite on top of circle
      const spriteIconMaterial = new SpriteMaterial({
        map: myLocationTexture,
        depthTest: false,
        transparent: true
      });
      const spriteIcon = new Sprite(spriteIconMaterial);
      spriteIcon.name = `spriteIcon_${cornerNames[i]}`;
      spriteIcon.position.set(0, -this.objConfig.cubeSize / 2, 0.1);
      spriteIcon.scale.set(this.objConfig.cubeSize / 2, this.objConfig.cubeSize / 2, this.objConfig.cubeSize / 2);
      spriteIcon.raycast = () => { };
      if (useLinkStyle) {
        spriteIcon.renderOrder = 10002;
      }

      cube.add(sprite);
      cube.add(spriteIcon);

      // Store the sprite reference for future access
      this.dragSprites[sprite.name] = sprite;

      // Add the cube to the mesh so it inherits transformations
      this.mesh.add(cube);

      // Store the cube reference for future access
      this.dragCubeCorners[cube.name] = cube;
    });

    // Create center handle (sphere)
    const sphereGeometry = new SphereGeometry(hitRadius, 8, 8);
    const sphereMaterial = new MeshBasicMaterial({
      color: 0x6B9D11, // green color
      opacity: 0,
      transparent: true
    });

    this.centerHandle = new Mesh(sphereGeometry, sphereMaterial);
    this.centerHandle.name = 'centerHandle';
    this.centerHandle.position.set(0, 0, useLinkStyle ? 1 : 0); // Center of the mesh
    this.centerHandle.raycast = () => { };
    if (useLinkStyle) {
      this.centerHandle.renderOrder = 10000;
    }

    // Create spherical background for center handle for better raycasting
    const centerSpriteRadius = sphereRadius * 1.1;
    const centerSpriteGeometry = new SphereGeometry(centerSpriteRadius, 16, 16);
    const centerSpriteMaterial = new MeshBasicMaterial({
      color: 0x6B9D11, // green color
      transparent: true,
      opacity: 0.5,
      depthTest: false,
      ...(useLinkStyle ? { depthWrite: false } : {}),
    });
    this.centerHandleSprite = new Mesh(centerSpriteGeometry, centerSpriteMaterial);
    this.centerHandleSprite.name = 'sprite_centerHandle';
    this.centerHandleSprite.position.set(0, 0, 0);
    this.centerHandleSprite.raycast = () => { };
    if (useLinkStyle) {
      this.centerHandleSprite.renderOrder = 10001;
    }

    // Create icon sprite on top of circle
    const centerSpriteIconMaterial = new SpriteMaterial({
      map: dragTexture,
      depthTest: false,
      transparent: true
    });
    this.centerHandleSpriteIcon = new Sprite(centerSpriteIconMaterial);
    this.centerHandleSpriteIcon.name = 'spriteIcon_centerHandle';
    this.centerHandleSpriteIcon.position.set(0, 0, 0.1); // Slightly in front
    this.centerHandleSpriteIcon.scale.set(sphereRadius, sphereRadius, sphereRadius);
    this.centerHandleSpriteIcon.raycast = () => { };
    if (useLinkStyle) {
      this.centerHandleSpriteIcon.renderOrder = 10002;
    }

    this.centerHandle.add(this.centerHandleSprite);
    this.centerHandle.add(this.centerHandleSpriteIcon);
    this.mesh.add(this.centerHandle);
    if (useLinkStyle) {
      this.syncHandleVisuals();
    }

    // Initially hide all handles
    this.hideDragHandles();
  }
  animateBorder() {
    this.borderMesh.material.opacity = .25;
  }
  removeBorder() {
    this.borderMesh.material.opacity = 0;
  }
  removeDragPoints() {
    Object.entries(this.dragSprites).forEach(([name, sprite]) => {
      if (this.dragSprites[name]) {
        this.dragSprites[name].parent?.remove(this.dragSprites[name]);
        this.dragSprites[name] = null;
      }
    });
    Object.entries(this.dragCubeCorners).forEach(([name, corner]) => {
      this.mesh.remove(this.dragCubeCorners[name]);
      this.dragCubeCorners[name] = null;
    });

    // Remove center handle
    if (this.centerHandle) {
      this.mesh.remove(this.centerHandle);
      this.centerHandle = null;
    }
    if (this.centerHandleSprite) {
      this.centerHandleSprite = null;
    }
    if (this.centerHandleSpriteIcon) {
      this.centerHandleSpriteIcon = null;
    }
  }

  shouldPlayOnClick(conf = this.dbConfig) {
    const v = conf?.playOnClick;
    return !(v === false || v === 'false' || v === 0 || v === '0');
  }

  /**
   * Open video modal and initialize video player
   */
  openModal() {
    if (this.placeAs3dElement) return;

    if (!this.videoUrl) {
      this.model.alert('error', $trans('no-video-uploaded-yet'));
      return;
    }

    const isFistLoad = !this.videoPlayer;
    if (!this.videoPlayer) {
      // Initialize Video.js
      this.videoPlayer = VideoJs(document.getElementById(`video-player-webgl-${this.uuid}`), {
        controls: true,
        autoplay: false,
        preload: 'auto',
        fluid: true,
        responsive: true,
        fill: false,
        controlBar: {
          playToggle: true,
          volumePanel: true,
          currentTimeDisplay: true,
          timeDivider: true,
          durationDisplay: true,
          progressControl: true,
          remainingTimeDisplay: false,
          pictureInPictureToggle: false,
          fullscreenToggle: true
        }
      });
    }

    const $el = {
      placeholder: $(`#video-placeholder-webgl-${this.uuid}`),
      loadingOverlay: $(`#video-loading-webgl-${this.uuid}`),
      progressBar: $(`#video-progress-bar-webgl-${this.uuid}`),
      progressText: $(`#video-progress-text-webgl-${this.uuid}`),
      videoPlayer: $(`#video-player-webgl-${this.uuid}`),
      video: this.modalWrapper.find("video"),
      title: this.modalWrapper.find(".video-window__title"),
      modal: this.modalWrapper,
    }

    // Set title
    const displayTitle = this.videoTitle || this.getFileNameFromUrl(this.videoUrl);
    $el.title.text(displayTitle);

    // Show modal
    $el.modal.addClass("is-active");

    const showVideo = () => {
      $el.loadingOverlay.hide();
      $el.placeholder.hide();
      $el.videoPlayer.show();
    }

    const showLoading = () => {
      $el.placeholder.show();
      $el.loadingOverlay.css('display', 'flex');
      $el.progressBar.css('width', '0%');
      $el.progressText.text('0%');
    }

    // On first load, set event listeners
    if (isFistLoad) {
      // Track loading progress
      this.videoPlayer.on('progress', () => {
        const buffered = this.videoPlayer.buffered();
        if (buffered.length > 0) {
          const duration = this.videoPlayer.duration();
          if (duration > 0) {
            const bufferedEnd = buffered.end(buffered.length - 1);
            const percentage = Math.round((bufferedEnd / duration) * 100);

            $el.progressBar.css('width', percentage + '%');
            $el.progressText.text(percentage + '%');
          }
        }
      });

      this.videoPlayer.on('playing', () => {
        showVideo();
      });

      this.videoPlayer.ready(() => {
        this.videoPlayer.play();
      });
    }

    const currentUrl = $el.video.attr('src');
    if (!currentUrl || currentUrl !== this.videoUrl) {
      if (this.videoRatio) {
        const paddingBottom = (1 / parseFloat(this.videoRatio)) * 100;
        $el.placeholder.css('padding-bottom', paddingBottom + '%');
        $el.placeholder.show();
      } else {
        // Default 16:9 if no ratio available
        $el.placeholder.css('padding-bottom', '56.25%');
        $el.placeholder.show();
      }

      // Hide video element initially
      $el.videoPlayer.hide();
    }

    // Show loading overlay
    showLoading();

    // Set video source and autoplay
    this.videoPlayer.src({
      src: this.videoUrl,
      type: this.getVideoType(this.videoUrl)
    });

    if (!isFistLoad) {
      this.videoPlayer.currentTime(0);
      this.videoPlayer.play();
      showVideo();
    }
  }

  /**
   * Close video modal and pause video
   */
  closeModal() {
    // Pause video
    if (this.videoPlayer) {
      this.videoPlayer.pause();
    }

    // Hide modal
    this.modalWrapper.removeClass("is-active");
  }

  /**
   * Get video MIME type from URL
   */
  getVideoType(url) {
    const extension = url.split('.').pop().toLowerCase().split('?')[0];
    const types = {
      'mp4': 'video/mp4',
      'webm': 'video/webm',
      'ogg': 'video/ogg',
      'mov': 'video/quicktime',
      'avi': 'video/x-msvideo',
      'm3u8': 'application/x-mpegURL'
    };
    return types[extension] || 'video/mp4';
  }

  /**
   * Extract filename from URL
   */
  getFileNameFromUrl(url) {
    return url.split('/').pop().split('?')[0];
  }

  /**
   * Set video URL and title
   */
  setVideoUrl(url, title = null, videoWidth = null, videoHeight = null) {
    const prevW = this.videoWidth;
    const prevH = this.videoHeight;
    this.videoUrl = url;
    this.videoTitle = title;
    if (videoWidth != null) this.videoWidth = videoWidth;
    if (videoHeight != null) this.videoHeight = videoHeight;
    if (this.placeAs3dElement && url) {
      const dimensionsChanged = (videoWidth != null && videoWidth !== prevW)
        || (videoHeight != null && videoHeight !== prevH);
      if (dimensionsChanged) {
        this.dbConfig.scene3dPerspectiveApplied = false;
      }
      this.applySceneVideoFromConfig({
        ...this.dbConfig,
        videoUrl: url,
        videoWidth: this.videoWidth,
        videoHeight: this.videoHeight,
      }, dimensionsChanged || !this.hasScene3dPerspectiveCorners());
    }
  }

  setVideoSource(video, url, onMetadataLoaded) {
    const isIOSSafari = isIOS() && isSafari();
    this.applySceneVideoLoop();
    video.src = url;
    video.load();
    video.addEventListener('loadedmetadata', () => {
      onMetadataLoaded && onMetadataLoaded(video.videoWidth, video.videoHeight);
      if (!isIOSSafari) {
        video.muted = false;
        video.volume = 1.0;
      }
      if (!this.model.editMode) {
        this._pendingPlaybackStart = true;
      }
      video.play();
    }, { once: true });
    if (!this.model.editMode) {
      if (!isIOSSafari) {
        video.muted = false;
        video.volume = 1.0;
      }
      this._pendingPlaybackStart = true;
      video.play().catch((error) => {
        this._pendingPlaybackStart = false;
        console.warn('Autoplay failed, video will remain muted:', error);
      });
    }
  }

  loadFirstFrame(video, url, onMetadataLoaded, onFrameReady = null) {
    const alreadyLoaded = this._sceneVideoLoadedUrl === url && video.readyState >= 2;
    if (alreadyLoaded) {
      onMetadataLoaded && onMetadataLoaded(video.videoWidth, video.videoHeight);
      if (this.sceneVideoTexture) {
        this.sceneVideoTexture.needsUpdate = true;
      }
      this.material.needsUpdate = true;
      onFrameReady && onFrameReady();
      return;
    }

    video.src = url;
    video.load();

    video.addEventListener('loadedmetadata', () => {
      video.currentTime = 0.1;
      onMetadataLoaded && onMetadataLoaded(video.videoWidth, video.videoHeight);

      const handleTimeUpdate = () => {
        if (video.currentTime >= 0.1) {
          video.pause();
          video.removeEventListener('timeupdate', handleTimeUpdate);
          if (this.sceneVideoTexture) {
            this.sceneVideoTexture.needsUpdate = true;
          }
          this.material.needsUpdate = true;
          onFrameReady && onFrameReady();
        }
      };

      video.addEventListener('timeupdate', handleTimeUpdate);
      video.play();
    }, { once: true });
  }

  updateGeometryWithVideoDimensions(w, h, firstTime) {
    // VRVideoGallery: fixed 256×256 plane; video size only drives corner positions.
    let aspectRatio = w / h;
    let halfW = w / 2;
    let halfH = h / 2;

    if (aspectRatio >= 1) {
      if (halfW > 128) {
        halfW = 128;
        halfH = halfW / aspectRatio;
      }
    } else {
      if (halfH > 64) {
        halfH = 64;
        halfW = halfH * aspectRatio;
      }
    }

    const corners = {
      topLeft: new Vector3(-halfW, halfH, 0),
      topRight: new Vector3(halfW, halfH, 0),
      bottomRight: new Vector3(halfW, -halfH, 0),
      bottomLeft: new Vector3(-halfW, -halfH, 0),
    };

    Object.entries(corners).forEach(([name, vector]) => {
      this.originalPerspectivePosition[name] = vector.clone();
      if (firstTime) {
        this.perspectivePosition[name] = vector.clone();
        this.dbConfig[name] = vector.clone();
        if (!this.dbConfig.original) this.dbConfig.original = {};
        this.dbConfig.original[name] = vector.clone();
        if (this.placeAs3dElement) {
          this.dbConfig.scene3dPerspectiveApplied = true;
        }
      }
    });
  }

  playOnClick() {
    if (!this.placeAs3dElement || this.model.editMode || !this.shouldPlayOnClick()) return;

    this._startSceneVideoPlayback();
  }

  pauseSceneVideoPlayback() {
    if (this.sceneVideo) {
      this.sceneVideo.pause();
    }
  }

  /**
   * Cleanup when object is destroyed
   */
  dispose() {
    // Dispose Video.js player
    if (this.videoPlayer) {
      this.videoPlayer.dispose();
      this.videoPlayer = null;
    }

    this.pauseSceneVideo();

    // Remove modal from DOM
    if (this.modalWrapper) {
      this.modalWrapper.remove();
    }

    super.dispose && super.dispose();
  }

}

ComponentRegistry.register({
  type: 'videoPlayerWebGL',
  displayNameKey: 'add-video-background-text',
  getDisplayName: (obj) => {
    const title = obj?.videoTitle != null ? String(obj.videoTitle).trim() : '';
    return title || $trans('add-video-player-text') || 'Video player';
  },
  events: [
    { value: 'click', labelKey: 'event-click' },
    { value: 'videoStartedPlaying', labelKey: 'event-video-started-playing' },
    { value: 'stoppedPlaying', labelKey: 'event-stopped-playing' },
    { value: 'raycastEnter', labelKey: 'event-raycast-enter' },
    { value: 'raycastLeave', labelKey: 'event-raycast-leave' },
  ],
  actions: [
    { value: 'playOnce', labelKey: 'action-play-once' },
    { value: 'playLoop', labelKey: 'action-play-loop' },
    { value: 'stopPlaying', labelKey: 'action-stop-playing' },
    { value: 'play', labelKey: 'action-play' },
    { value: 'pause', labelKey: 'action-pause' },
    { value: 'closeElement', labelKey: 'action-close-element' },
  ],
});

export { VRVideoPlayerWebGL };
